import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

/**
 * @typedef {Object} VideoCardRefs
 * @property {HTMLElement} [muteBtn] - Mute toggle button
 * @property {HTMLElement} [expandBtn] - Expand/fullscreen button
 * @property {HTMLDialogElement} [expandDialog] - Fullscreen dialog overlay
 * @property {HTMLElement} [expandCloseBtn] - Close button inside expand dialog
 * @property {HTMLVideoElement} [expandVideo] - Video element inside expand dialog
 */

/** @extends {Component<VideoCardRefs>} */
class VideoCardComponent extends Component {
  #abortController = new AbortController();

  /** @type {number | undefined} Failsafe for a video whose metadata never arrives. */
  #sizingTimeout;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    this.addEventListener(ThemeEvents.mediaStartedPlaying, this.#onMediaStarted, { signal });
    this.addEventListener("play", this.#onVideoPlay, { signal, capture: true });
    this.refs.expandDialog?.addEventListener("close", this.#onExpandDialogClose, { signal });
    this.refs.expandVideo?.addEventListener("loadedmetadata", this.#onExpandVideoSized, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #onMediaStarted = () => {
    this.#pauseOtherVideoCards();

    const muteBtn = this.refs.muteBtn;
    if (muteBtn instanceof HTMLElement) {
      muteBtn.classList.remove("hidden");
    }

    const expandBtn = this.refs.expandBtn;
    if (expandBtn instanceof HTMLElement) {
      expandBtn.classList.remove("hidden");
    }
  };

  /**
   * @param {Event} event
   */
  #onVideoPlay = event => {
    if (!(event.target instanceof HTMLVideoElement)) return;

    this.#pauseOtherVideoCards();
  };

  #pauseOtherVideoCards() {
    const videoCards = document.querySelectorAll("video-card-component");

    for (const videoCard of videoCards) {
      if (videoCard === this) continue;

      videoCard.querySelectorAll("video").forEach(video => video.pause());
      videoCard.querySelectorAll("deferred-media").forEach(media => {
        if ("pauseMedia" in media && typeof media.pauseMedia === "function") {
          media.pauseMedia();
        }
      });
    }
  }

  /**
   * True while a dialog opened from an already playing card is on screen, so the
   * card can pick playback up again when it closes. A popup opened straight from
   * the poster must not set this: nothing was playing inline, and resuming would
   * start the cropped inline video that the popup exists to avoid.
   * @type {boolean}
   */
  #resumeInlineOnClose = false;

  expandMedia() {
    const dialog = this.refs.expandDialog;
    const expandVideo = this.refs.expandVideo;
    if (!(dialog instanceof HTMLDialogElement) || !(expandVideo instanceof HTMLVideoElement)) return;

    const inlineVideo = this.querySelector("video");
    if (!inlineVideo) return;

    // Clone the source(s) from the inline video into the dialog video
    expandVideo.removeAttribute("src");
    while (expandVideo.firstChild) expandVideo.removeChild(expandVideo.firstChild);

    if (inlineVideo.src) {
      expandVideo.src = inlineVideo.src;
    } else {
      for (const source of inlineVideo.querySelectorAll("source")) {
        expandVideo.appendChild(source.cloneNode(true));
      }
    }

    expandVideo.currentTime = inlineVideo.currentTime;
    expandVideo.muted = inlineVideo.muted;
    expandVideo.loop = inlineVideo.loop;

    // Pause the inline video. Closing the dialog hands playback back to it.
    inlineVideo.pause();
    this.#resumeInlineOnClose = true;

    // Lock scroll via the theme's native convention (html[scroll-lock] { overflow: hidden }).
    // This keeps the scroll position in place, so closing the dialog doesn't jump the page.
    document.documentElement.setAttribute("scroll-lock", "");

    this.#awaitVideoSize(expandVideo);
    dialog.showModal();
    expandVideo.play().catch(() => {});
  }

  /**
   * Opens the expand dialog from the poster button, sourcing video tracks
   * from the deferred-media <template> so it works before inline playback begins.
   */
  openExpandFromPoster() {
    const dialog = this.refs.expandDialog;
    const expandVideo = this.refs.expandVideo;
    if (!(dialog instanceof HTMLDialogElement) || !(expandVideo instanceof HTMLVideoElement)) return;

    // Read video sources from the <template> inside <deferred-media> — the inline
    // video has never loaded at this point, so we cannot source from this.querySelector('video').
    const template = this.querySelector("deferred-media template");
    if (!template) return;

    const templateVideo = template.content.querySelector("video");
    if (!templateVideo) return;

    // Clone the source(s) from the template video into the dialog video.
    expandVideo.removeAttribute("src");
    while (expandVideo.firstChild) expandVideo.removeChild(expandVideo.firstChild);

    if (templateVideo.src) {
      expandVideo.src = templateVideo.src;
    } else {
      for (const source of templateVideo.querySelectorAll("source")) {
        expandVideo.appendChild(source.cloneNode(true));
      }
    }

    expandVideo.currentTime = 0;
    this.#resumeInlineOnClose = false;

    // Reflect the block's own "Mute by default" setting, not any inline video state.
    expandVideo.muted = this.dataset.playInPopupMuted === "true";

    // Mirror the loop setting from the template video.
    expandVideo.loop = templateVideo.loop;

    // Pause any other playing video cards on the page.
    this.#pauseOtherVideoCards();

    // Lock scroll so the page position does not shift while the dialog is open.
    document.documentElement.setAttribute("scroll-lock", "");

    this.#awaitVideoSize(expandVideo);
    dialog.showModal();
    expandVideo.play().catch(() => {});
  }

  /**
   * Holds the dialog video back until its real dimensions are known.
   *
   * A video element with no source measures 300x150, the browser's default, and
   * only takes the movie's own shape once metadata arrives. Left visible it is
   * painted at the default size first and then jumps to the real one, which reads
   * as the player growing as it opens. A minimum size cannot fix that — it only
   * changes the size it grows from — so the element is held invisible for the one
   * frame it would be wrong, and its layout box is reserved either way.
   *
   * @param {HTMLVideoElement} video
   */
  #awaitVideoSize(video) {
    clearTimeout(this.#sizingTimeout);
    video.dataset.awaitingSize = "true";

    // A video that never reports metadata (a failed source, a codec the browser
    // will not decode) must not be left hidden behind the backdrop.
    this.#sizingTimeout = setTimeout(() => this.#onExpandVideoSized(), 2000);
  }

  #onExpandVideoSized = () => {
    clearTimeout(this.#sizingTimeout);
    delete this.refs.expandVideo?.dataset.awaitingSize;
  };

  closeExpand() {
    this.refs.expandDialog?.close();
  }

  /** @type {() => void} */
  #onExpandDialogClose = () => {
    clearTimeout(this.#sizingTimeout);

    const expandVideo = this.refs.expandVideo;

    // Read where the viewer got to before the dialog video is torn down.
    const resumeAt = expandVideo instanceof HTMLVideoElement ? expandVideo.currentTime : 0;
    const resumeMuted = expandVideo instanceof HTMLVideoElement ? expandVideo.muted : true;

    if (expandVideo instanceof HTMLVideoElement) {
      expandVideo.pause();
      expandVideo.removeAttribute("src");
      while (expandVideo.firstChild) expandVideo.removeChild(expandVideo.firstChild);
      expandVideo.load();
    }

    // Release the scroll lock. No scroll restoration needed — the position never moved.
    document.documentElement.removeAttribute("scroll-lock");

    if (!this.#resumeInlineOnClose) return;
    this.#resumeInlineOnClose = false;

    // The card was playing when the dialog opened, so carry the viewer's position
    // and sound choice back to it rather than leaving a paused card behind.
    const inlineVideo = this.querySelector("video");
    if (!inlineVideo) return;

    if (Number.isFinite(resumeAt)) inlineVideo.currentTime = resumeAt;
    inlineVideo.muted = resumeMuted;
    this.#syncMuteButton(inlineVideo.muted);
    inlineVideo.play().catch(() => {});
  };

  /**
   * Keeps the card's mute control telling the truth about the video.
   * @param {boolean} muted
   */
  #syncMuteButton(muted) {
    const muteBtn = this.refs.muteBtn;
    if (!muteBtn) return;

    const ariaLabel = muted ? this.#unmuteLabel : this.#muteLabel;
    if (ariaLabel) muteBtn.setAttribute("aria-label", ariaLabel);
    muteBtn.dataset.muted = muted ? "true" : "false";
  }

  toggleMute() {
    const video = this.querySelector("video");
    if (!video) return;
    video.muted = !video.muted;
    this.#syncMuteButton(video.muted);
  }

  get #muteLabel() {
    return this.dataset.muteLabel || "";
  }

  get #unmuteLabel() {
    return this.dataset.unmuteLabel || "";
  }
}

if (!customElements.get("video-card-component")) {
  customElements.define("video-card-component", VideoCardComponent);
}
